import {
  Button,
  Flex,
  HStack,
  Menu,
  Text,
  useDisclosure,
  Drawer,
} from '@chakra-ui/react';

import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { IoWarning } from 'react-icons/io5';
import { useAccount, useBalance, useChainId, useDisconnect } from 'wagmi';
import { HeaderMenuButton } from './parts/HeaderMenuButton';
import { HeaderLinkWrapper } from '@/components/Layouts/parts/Header/parts/HeaderLinkWrapper';
import { SwitchLanguageButton } from '@/components/Layouts/parts/Header/parts/SwitchLanguageButton';
import ConnectModal from '@/components/Modal/Connect';
import { network } from '@/constants/network';
import { useClientReady } from '@/features/common/hooks/useClientReady';

export const Header = () => {
  const NetworkIcon = network.icon;
  const { address, isConnected } = useAccount();
  const { data: balanceData } = useBalance({
    address,
  });
  const { disconnectAsync } = useDisconnect();
  const chainId = useChainId();
  const router = useRouter();
  const formattedAddress = address
    ? `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
    : '';
  const { t } = useTranslation(['common']);
  const isClientReady = useClientReady();
  const {
    open: isOpenConnectModal,
    onOpen: onOpenConnectModal,
    onClose: onCloseConnectModal,
  } = useDisclosure();
  const { open: isOpenMenu, onOpen: onOpenMenu, onClose: onCloseMenu } = useDisclosure();

  const onDisconnect = async () => {
    await disconnectAsync();
    router.replace('/');
  };

  return (
    <HStack
      position="sticky"
      top={0}
      left={0}
      py={{ base: 3, md: 8 }}
      pl={8}
      pr={{ base: 5, md: 8 }}
      alignItems="center"
      zIndex={{ base: 1500, md: 1 }}
      w="full"
    >
      <Drawer.Root
        placement="end"
        open={isOpenMenu}
        onOpenChange={(e) => {
          console.log('Drawer onOpenChange:', e);
          if (e.open) {
            console.log('Opening menu');
            onOpenMenu();
          } else {
            console.log('Closing menu');
            onCloseMenu();
          }
        }}
      >
        <Drawer.Backdrop style={{ zIndex: 1400 }} />
        <Drawer.Positioner style={{ zIndex: 1400 }}>
          <Drawer.Content maxW="264px">
            <Drawer.CloseTrigger />
            <Drawer.Body pt={24} background="brand.offWhite">
              <Flex alignItems="center" flexDir="row">
                <SwitchLanguageButton />
                <HeaderLinkWrapper />
              </Flex>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
      <Flex flexDir="row" alignItems="center" justifyContent="flex-end" w="100%">
        <Flex flexDir="row" alignItems="center" justifyContent="flex-end" w="auto">
          <Flex display={{ base: 'none', md: 'flex' }} flexDir="row" py={1} w="auto">
            <HeaderLinkWrapper />
          </Flex>
          <Flex display={{ base: 'none', md: 'flex' }} flexDir="row" px={6} py={1} w="auto">
            <SwitchLanguageButton />
          </Flex>
          <Flex alignItems="center" w="auto" pr={{ base: '20px', md: '0' }}>
            {isClientReady &&
              (isConnected ? (
                <>
                  <Flex
                    flexDir="row"
                    px={{ base: '0.6rem', md: 4 }}
                    py={2}
                    borderRadius={16}
                    w={{ base: '110px', md: 'auto' }}
                    alignItems="center"
                    background="linear-gradient(to right, var(--chakra-colors-brand-purple-100), var(--chakra-colors-brand-pink))"
                    ml="2px"
                  >
                    {chainId === network.chainId ? (
                      <NetworkIcon boxSize={4} />
                    ) : (
                      <IoWarning size="1rem" />
                    )}

                    <Text ml={2} fontWeight="semibold" fontSize="xs" color="brand.staticWhite">
                      {chainId === network.chainId
                        ? network.name
                        : t('common.networks.unsupported.name')}
                    </Text>
                  </Flex>

                  <Menu.Root>
                    <Menu.Trigger asChild>
                      <HeaderMenuButton
                        address={formattedAddress}
                        balancedata={balanceData}
                        px={{ base: 4, md: 2 }}
                        ml={{ base: '8px', md: 4 }}
                      />
                    </Menu.Trigger>
                    <Menu.Positioner>
                      <Menu.Content>
                        <Menu.Item value="disconnect" onClick={onDisconnect}>
                          {t('common.components.elements.buttons.disconnect')}
                        </Menu.Item>
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                </>
              ) : (
                <Button
                  size="xs"
                  bg="transparent"
                  borderWidth="1px"
                  borderColor="brand.staticWhite"
                  color="brand.staticWhite"
                  px={6}
                  py={4}
                  borderRadius={16}
                  onClick={onOpenConnectModal}
                  cursor="pointer"
                  _hover={{
                    bg: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {t('common.components.elements.buttons.connect')}
                </Button>
              ))}

            <Text
              as="span"
              cursor="pointer"
              display={{ base: 'block', md: 'none' }}
              w={isOpenMenu ? '30px' : '18px'}
              h={isOpenMenu ? '30px' : '9px'}
              position="absolute"
              right="12px"
              zIndex="9999"
              style={{ pointerEvents: 'auto' }}
              onClick={() => {
                console.log('Hamburger menu clicked, isOpenMenu:', isOpenMenu);
                if (isOpenMenu) {
                  console.log('Calling onCloseMenu');
                  onCloseMenu();
                } else {
                  console.log('Calling onOpenMenu');
                  onOpenMenu();
                }
              }}
            >
              <Text
                as="span"
                display="inline-block"
                transition="all 0.4s"
                position="absolute"
                top={isOpenMenu ? '22px' : 0}
                right="0"
                margin="0"
                height="1px"
                background={isOpenMenu ? 'linear-gradient(135deg,#fc2b60 0,#db9e37 100%)' : '#fff'}
                transform={isOpenMenu ? 'translateY(-6px) rotate(45deg)' : 'none'}
                w="100%"
              ></Text>
              <Text
                as="span"
                display="inline-block"
                transition="all 0.4s"
                position="absolute"
                bottom={isOpenMenu ? '20px' : 0}
                right="0"
                margin="0"
                height="1px"
                background={isOpenMenu ? 'linear-gradient(135deg,#fc2b60 0,#db9e37 100%)' : '#fff'}
                transform={isOpenMenu ? 'translateY(6px) rotate(-45deg)' : 'none'}
                w="100%"
              ></Text>
            </Text>
          </Flex>
        </Flex>
      </Flex>
      {isClientReady && <ConnectModal isOpen={isOpenConnectModal} onClose={onCloseConnectModal} />}
    </HStack>
  );
};
